import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { useAuth, formatApiError } from "@/context/AuthContext";
import api from "@/lib/api";
import { HOME_BY_ROLE } from "@/components/ProtectedRoute";
import { Sparkles, Lock, Heart } from "lucide-react";
import { toast } from "sonner";
import { usePageMeta } from "@/lib/usePageMeta";

export default function Login() {
  const { login } = useAuth();
  usePageMeta("Sign in · Suffle Online", "Sign in to Suffle Online — the AI-powered marketplace. Shop, discover and shop from verified sellers with Suffle AI.");
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [needsVerify, setNeedsVerify] = useState(false);

  useEffect(() => {
    const v = sp.get("verified");
    if (v === "1") toast.success("Email verified! You can log in now.");
    else if (v === "expired") toast.error("That verification link expired. Please resend it.");
    else if (v === "0") toast.error("Invalid verification link.");
    const lk = sp.get("locked");
    if (lk === "1") toast.success("Your account is locked and the email change was undone. Check your inbox to reset your password.", { duration: 8000 });
    else if (lk === "expired") toast.error("That security link is invalid or has expired.");
  }, [sp]);

  const resend = async () => {
    try { await api.post("/auth/resend-verification", { email, app_origin: window.location.origin }); toast.success("Verification email sent. Check your inbox."); }
    catch (e) { toast.error("Could not resend right now."); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setNeedsVerify(false); setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}!`);
      const next = sp.get("next");
      const dest = next && next.startsWith("/") ? next : (u.role === "customer" ? "/" : (HOME_BY_ROLE[u.role] || "/app/home"));
      navigate(dest);
    } catch (err) {
      const detail = formatApiError(err.response?.data?.detail) || "Login failed";
      setError(detail);
      if (/verify your email/i.test(detail)) setNeedsVerify(true);
    } finally { setLoading(false); }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden flex-col justify-center gap-10 overflow-hidden p-12 lg:flex" style={{ backgroundColor: "#0b0a1f", backgroundImage: "radial-gradient(55% 55% at 12% 8%, rgba(37,99,235,.5), transparent 60%), radial-gradient(55% 55% at 88% 12%, rgba(124,58,237,.5), transparent 60%), radial-gradient(70% 60% at 60% 100%, rgba(219,39,119,.28), transparent 60%)" }}>
        <span className="inline-block w-fit [filter:drop-shadow(0_0_6px_rgba(255,255,255,0.5))_drop-shadow(0_0_18px_rgba(124,110,255,0.5))]"><Logo variant="xl" /></span>
        <div>
          <h2 className="font-brand text-4xl font-extrabold leading-tight text-white xl:text-5xl">Shop. Discover.<br /><span className="brand-gradient-text">Suffle.</span></h2>
          <p className="mt-3 max-w-sm text-white/70">Sign in to Suffle Online — the AI-powered marketplace. Shop, discover and track your orders from one place.</p>
          <div className="mt-6 flex flex-wrap gap-4 text-sm text-white/70">
            <span className="flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-purple-400" /> AI-Powered</span>
            <span className="flex items-center gap-1.5"><Lock className="h-4 w-4 text-emerald-400" /> Secure &amp; Private</span>
            <span className="flex items-center gap-1.5"><Heart className="h-4 w-4 text-rose-400" /> Built for Real Life</span>
          </div>
        </div>
        <p className="text-xs text-white/50">© 2026 Suffle Online · Naucera Travel Private Limited</p>
      </div>

      <div className="flex items-center justify-center p-6">
        <motion.form initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden"><Logo variant="lg" /></div>
          <h1 className="mt-6 font-brand text-2xl font-bold text-slate-800">Welcome back</h1>
          <p className="mt-1 text-sm text-slate-500">Log in to continue to Suffle Online.</p>

          {error && <div className="mt-4 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-600" data-testid="login-error">{error}</div>}
          {needsVerify && (
            <button type="button" onClick={resend} data-testid="resend-verification"
              className="mt-2 w-full rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-100">
              Resend verification email
            </button>
          )}

          <label className="mt-5 block text-sm font-medium text-slate-600">Email</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required data-testid="login-email"
            className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-2.5 outline-none focus:border-blue-400" placeholder="you@example.com" />

          <div className="mt-4 flex items-center justify-between">
            <label className="text-sm font-medium text-slate-600">Password</label>
            <Link to="/forgot-password" className="text-xs font-medium text-blue-600 hover:text-blue-700" data-testid="forgot-link">Forgot password?</Link>
          </div>
          <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required data-testid="login-password"
            className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-2.5 outline-none focus:border-blue-400" placeholder="••••••••" />

          <button type="submit" disabled={loading} data-testid="login-submit"
            className="mt-6 w-full rounded-xl brand-gradient-bg py-3 font-semibold text-white disabled:opacity-50">
            {loading ? "Signing in..." : "Log In"}
          </button>

          <p className="mt-4 text-center text-sm text-slate-500">New to Suffle? <Link to="/register" className="font-semibold text-blue-600">Create an account</Link></p>
        </motion.form>
      </div>
    </div>
  );
}
